import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, signOut as supabaseSignOut } from '../lib/supabase.js';

const AuthContext = createContext({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data?.session ?? null);
        setLoading(false);
      })
      .catch((error) => {
        console.error('AuthProvider getSession error', error);
        if (!mounted) return;
        setLoading(false);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      // Some Supabase events (TOKEN_REFRESHED while offline, INITIAL_SESSION
      // before the session is fully restored, etc.) fire with nextSession=null
      // even though the user is still authenticated. Don't blindly null-out
      // the session unless this is an explicit SIGNED_OUT / USER_DELETED event;
      // otherwise the UI sees user=null mid-onboarding and downstream code
      // throws "Missing auth user" right when the user clicks the final step.
      if (nextSession) {
        setSession(nextSession);
      } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setSession(null);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription?.subscription?.unsubscribe?.();
    };
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signOut: async () => {
        await supabaseSignOut();
        setSession(null);
      },
    }),
    [session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
